/**
 * First fall frost normals by location, for timing fall seeding and the
 * last mows of the year. Data: NOAA NCEI 1991-2020 Climate Normals, median
 * first fall 32°F and 28°F dates, per 3-digit ZIP prefix
 * (lib/frost-normals.generated.ts, built by scripts/generate-frost-normals.mjs).
 *
 * Imports only the generated table, so the mobile app can use it through
 * `@shared/calculators/frost-normals`.
 */

import { FROST_NORMALS_ZIP3 } from "./frost-normals.generated"

export interface FrostNormals {
  /** Median day of year (non-leap) of the first fall 32°F night. Above 365 = next year. Null = frost-free. */
  first32Doy: number | null
  /** Median day of year of the first fall 28°F night (hard freeze). Null = none in the median year. */
  first28Doy: number | null
  /** ZIP prefix the normals came from. */
  zip3: number
}

type Row = readonly [number, number, number, number, number]

const BY_ZIP3 = new Map<number, Row>(FROST_NORMALS_ZIP3.map((row) => [row[0], row]))

function toNormals(row: Row): FrostNormals {
  return {
    zip3: row[0],
    first32Doy: row[3] > 0 ? row[3] : null,
    first28Doy: row[4] > 0 ? row[4] : null,
  }
}

/** Frost normals for a 5-digit ZIP (or its 3-digit prefix). Null when unknown. */
export function frostNormalsForZip(zip: string | null | undefined): FrostNormals | null {
  if (!zip) return null
  const digits = zip.trim().slice(0, 3)
  if (!/^\d{3}$/.test(digits)) return null
  const row = BY_ZIP3.get(Number(digits))
  return row ? toNormals(row) : null
}

/** Frost normals for the ZIP prefix whose centroid is nearest this point (within ~150 km). */
export function frostNormalsForPoint(
  latitude: number | null | undefined,
  longitude: number | null | undefined,
): FrostNormals | null {
  if (latitude == null || longitude == null || !Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return null
  }
  const cosLat = Math.cos((latitude * Math.PI) / 180)
  let best: Row | null = null
  let bestD = Infinity
  for (const row of FROST_NORMALS_ZIP3) {
    const dLat = row[1] - latitude
    const dLon = (row[2] - longitude) * cosLat
    const d = dLat * dLat + dLon * dLon
    if (d < bestD) {
      bestD = d
      best = row
    }
  }
  // ~1.35 degrees is about 150 km.
  if (!best || bestD > 1.35 * 1.35) return null
  return toNormals(best)
}

const MONTH_START = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334]

/** The calendar date of a normals day-of-year in the fall of `year`. */
export function dateFromDoy(doy: number, year: number): Date {
  let y = year
  let d = doy
  if (d > 365) {
    y += 1
    d -= 365
  }
  let month = 11
  while (month > 0 && MONTH_START[month] >= d) month -= 1
  return new Date(y, month, d - MONTH_START[month], 12)
}

/** Day of year (non-leap numbering) of a date. Feb 29 counts as Feb 28. */
export function doyOf(date: Date): number {
  const month = date.getMonth()
  const day = month === 1 && date.getDate() === 29 ? 28 : date.getDate()
  return MONTH_START[month] + day
}
